import { useState } from 'react';
import { Alert, Dialog, Link, Stack, t, Text, TextField, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { pendingLabel } from './PendingButton';
import { openPullRequestOf, PullRequestConflict } from './PullRequestConflict';
import { changeRef } from './catalogue/PullRequestNotice';

export type ServerRename = maintenance.components['schemas']['ServerRename'];

const SERVER_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * D22 « Renommer »: the portal proposes `<client>-<project>-<role>-<env>-<NN>`,
 * the person may change it. A pull request renames the server in the
 * catalogue; once merged, the portal renames the VPS at OVHcloud and the
 * Checkmk host, and carries the server's records over to the new name.
 */
export function RenameDialog({ rename, onClose, onOpened, onStepUp }: { rename: ServerRename; onClose: () => void; onOpened: () => void; onStepUp: () => void }) {
  const [name, setName] = useState(rename.suggestion ?? '');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const to = name.trim();
  const invalid = to !== '' && !SERVER_NAME.test(to);
  // Server names are unique: two servers under one name would be one Checkmk host, one line in the lists.
  const taken = to !== rename.server && rename.taken.includes(to);

  const submit = async () => {
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.POST('/servers/{server}/rename', { params: { path: { server: rename.server } }, body: { name: to } }));
      toast({ tone: 'pos', title: t('rename.opened', { ref: changeRef(pullRequest) }) });
      onOpened();
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') onStepUp();
      else if (e instanceof ApiFailure) setFailure(e);
      else throw e;
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      onClose={onClose}
      title={t('rename.title', { server: rename.server })}
      description={t('rename.lead')}
      confirmLabel={pendingLabel(t('rename.confirm'), sending)}
      confirmDisabled={!to || invalid || taken || to === rename.server || sending}
      onConfirm={() => void submit()}
    >
      <Stack gap={16}>
        <TextField
          label={t('rename.name')}
          value={name}
          mono
          onChange={(e: { target: { value: string } }) => { setName(e.target.value.toLowerCase()); setFailure(null); }}
          hint={rename.suggestion ? t('rename.hint', { suggestion: rename.suggestion }) : t('rename.hintNoSuggestion')}
          error={invalid ? t('rename.invalid') : taken ? t('rename.taken', { name: to }) : undefined}
          required
        />
        <Stack gap={4}>
          <Text variant="body-sm">{t('rename.after.title')}</Text>
          <Text variant="body-sm" tone="mute">{t('rename.after.provider', { ref: rename.providerRef ?? '—' })}</Text>
          <Text variant="body-sm" tone="mute">{t('rename.after.checkmk')}</Text>
          <Text variant="body-sm" tone="mute">{t('rename.after.records')}</Text>
        </Stack>
        <Alert tone="info">{t('rename.hostname')}</Alert>
        {failure && openPullRequestOf(failure) ? (
          <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />
        ) : failure && (
          <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>
        )}
      </Stack>
    </Dialog>
  );
}

/** Where the rename stands, from its pull request to the new name read in the catalogue. */
export function RenameProgress({ rename }: { rename: ServerRename }) {
  const pr = rename.pullRequest;
  if (!pr || pr.state === 'closed') return null;
  const failed = [
    ...(pr.steps?.provider ? [t('rename.progress.provider', { error: pr.steps.provider })] : []),
    ...(pr.steps?.migrations ?? []).filter(m => m.error).map(m => t('rename.progress.part', { part: m.part, error: m.error ?? '' })),
  ];
  const link = <Link href={pr.url} external>{t('pullRequest.link', { ref: changeRef(pr) })}</Link>;
  if (pr.state === 'open' || pr.state === 'validation_failed') {
    return (
      <Alert tone={pr.state === 'open' ? 'info' : 'warn'} title={t(pr.state === 'open' && pr.autoMerge ? 'rename.progress.autoMerge' : `rename.progress.${pr.state}`, { to: pr.to })}>
        <Stack gap={4}>{pr.ciMessage && <Text variant="body-sm">{pr.ciMessage}</Text>}{link}</Stack>
      </Alert>
    );
  }
  if (pr.state === 'applied') {
    return (
      <Alert tone="pos" title={t('rename.progress.applied', { to: pr.to })}>
        <Link href={`/servers/${encodeURIComponent(pr.to)}`}>{t('rename.progress.openNew', { to: pr.to })}</Link>
      </Alert>
    );
  }
  // Merged: the portal's part runs, then the OVHcloud sync brings the new name.
  return (
    <Alert tone={failed.length ? 'warn' : 'info'} title={t(pr.executed ? 'rename.progress.syncing' : failed.length ? 'rename.progress.failed' : 'rename.progress.merged', { to: pr.to })}>
      <Stack gap={4}>
        {failed.map(f => <Text key={f} variant="body-sm">{f}</Text>)}
        {failed.length > 0 && <Text variant="body-sm" tone="mute">{t('rename.progress.retry')}</Text>}
        {pr.executed && <Text variant="body-sm" tone="mute">{t('rename.progress.agent')}</Text>}
        {link}
      </Stack>
    </Alert>
  );
}
